import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

const MatchVote = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [dateRange, setDateRange] = useState([]);
  const [showBackButton, setShowBackButton] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const sports = [
    { value: 'all', label: 'All Sports' },
    { value: 'football', label: 'Football' },
    { value: 'cricket', label: 'Cricket' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'basketball', label: 'Basketball' },
    { value: 'badminton', label: 'Badminton' },
    { value: 'volleyball', label: 'Volleyball' },
    { value: 'hockey', label: 'Hockey' }
  ];

  // Hard cap for a live match: 3 minutes
  const MAX_LIVE_DURATION_MS = 3 * 60 * 1000;

  // Tick every second for live/countdown
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Floating back button visibility
  useEffect(() => {
    const handleScroll = () => setShowBackButton(window.scrollY > 200);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Build date strip
  useEffect(() => {
    generateDateRange();
  }, []);

  // Fetch when filters change
  useEffect(() => {
    if (selectedDate) fetchMatches();
  }, [selectedDate, selectedSport]);

  // Auto-center to today
  useEffect(() => {
    if (dateRange.length > 0) setTimeout(scrollToToday, 100);
  }, [dateRange]);

  // Optional legacy parser (kept for compatibility)
  const parseLocalDateTime = (str) => {
    if (!str) return null;
    try {
      if (str.includes('T') && str.includes('Z')) {
        const date = new Date(str);
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      }
      const cleanStr = str.replace(/Z$/, '').split('.')[0];
      if (cleanStr.includes('T')) {
        const [datePart, timePart] = cleanStr.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
        return new Date(year, month - 1, day, hour || 0, minute || 0, 0, 0);
      }
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  // Simple parser for display/math
  const parseDate = (str) => (str ? new Date(str) : null);

  // Elapsed clock mm:ss or h:mm:ss
  const formatElapsed = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    if (minutes > 0) return `${minutes}:${String(seconds).padStart(2, '0')}`;
    return `0:${String(seconds).padStart(2, '0')}`;
  };

  // Live elapsed (capped at 3:00)
  const getMatchElapsedTime = (startTime, endTime, status) => {
    if (status !== 'live') return null;
    const start = parseDate(startTime);
    if (!start) return null;
    const diffMs = Date.now() - start.getTime();
    if (diffMs < 0 || diffMs >= MAX_LIVE_DURATION_MS) return null;
    return formatElapsed(diffMs);
  };

  // Client-side computed status
  const getComputedStatusAndEnd = (status, startTime, endTime) => {
    const start = parseDate(startTime)?.getTime() ?? null;
    const now = Date.now();
    const providedEnd = parseDate(endTime)?.getTime() ?? null;
    const capEnd = start != null ? start + MAX_LIVE_DURATION_MS : null;

    if (status === 'ended') return { status: 'ended', endTime: endTime ?? (capEnd ? new Date(capEnd).toISOString() : undefined) };
    if (start == null) return { status, endTime };
    if (now < start) return { status: 'upcoming', endTime: endTime ?? (capEnd ? new Date(capEnd).toISOString() : undefined) };

    const effectiveEnd = providedEnd != null ? (capEnd != null ? Math.min(providedEnd, capEnd) : providedEnd) : capEnd;
    if (effectiveEnd != null && now >= effectiveEnd) return { status: 'ended', endTime: new Date(effectiveEnd).toISOString() };
    return { status: 'live', endTime: effectiveEnd != null ? new Date(effectiveEnd).toISOString() : undefined };
  };

  // Date strip generator
  const generateDateRange = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = -730; i <= 730; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      dates.push({
        date: dateString,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
        dayNum: date.getDate(),
        monthName: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
        year: date.getFullYear(),
        isToday: i === 0
      });
    }
    setDateRange(dates);
    if (!selectedDate) {
      const todayDate = dates.find((d) => d.isToday);
      if (todayDate) setSelectedDate(todayDate.date);
    }
  };

  // Center to today
  const scrollToToday = () => {
    const container = document.querySelector('.date-scroll-container');
    const todayButton = document.querySelector('[data-is-today="true"]');
    if (!container || !todayButton) return;
    const containerWidth = container.offsetWidth;
    const buttonOffsetLeft = todayButton.offsetLeft;
    const buttonWidth = todayButton.offsetWidth;
    const scrollPosition = buttonOffsetLeft - containerWidth / 2 + buttonWidth / 2;
    container.scrollTo({ left: Math.max(0, scrollPosition), behavior: 'smooth' });
  };

  // Fetch matches for selected day and sport (combined: manual + SofaScore)
  const fetchMatches = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const tzOffsetMinutes = -new Date().getTimezoneOffset();
      let url = `${API_BASE_URL}/api/combined-matches?date=${selectedDate}&tzOffsetMinutes=${tzOffsetMinutes}`;
      if (selectedSport !== 'all') url += `&sport=${selectedSport}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setMatches(data);
      } else {
        setMatches([]);
      }
    } catch (e) {
      console.error('Error fetching matches:', e);
      setMatches([]);
    }
    setLoading(false);
  };

  // Display time in 12-hour with minutes
  const formatTime = (str) => {
    const d = parseDate(str);
    if (!d || Number.isNaN(d.getTime())) return 'TBD';
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Countdown label for upcoming
  const getTimeLeftLabel = (startTime) => {
    const start = parseDate(startTime);
    if (!start) return null;
    const diff = start.getTime() - currentTime.getTime();
    if (diff <= 0) return null;

    const totalSec = Math.floor(diff / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}h left`;
    return `${minutes}:${String(seconds).padStart(2, '0')} left`;
  };

  // Status badge with countdown and start-time-under-ended
  const getStatusBadge = (status, startTime, endTime) => {
    const elapsedTime = getMatchElapsedTime(startTime, endTime, status);
    const timeLeft = status === 'upcoming' ? getTimeLeftLabel(startTime) : null;

    switch (status) {
      case 'live':
        return (
          <div className="flex flex-col items-center space-y-0.5 leading-tight">
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold text-red-600 bg-red-100 rounded">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1 animate-pulse"></span>
              LIVE
            </span>
            {elapsedTime && (
              <div className="text-[10px] font-mono font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                {elapsedTime}
              </div>
            )}
          </div>
        );
      case 'ended':
        return (
          <div className="flex flex-col items-center space-y-0.5 leading-tight">
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold text-gray-600 bg-gray-100 rounded">
              ENDED
            </span>
            {/* Show START time under ENDED */}
            <span className="text-xs text-gray-700 font-medium">{formatTime(startTime)}</span>
          </div>
        );
      case 'upcoming':
      default:
        return (
          <div className="flex flex-col items-center leading-tight">
            <span className="text-xs text-gray-700 font-medium">{formatTime(startTime)}</span>
            {timeLeft && (
              <span className="text-[10px] text-blue-700 font-semibold bg-blue-50 px-1.5 py-0.5 rounded mt-0.5">
                {timeLeft}
              </span>
            )}
          </div>
        );
    }
  };

  // Group by Sport -> Event
  const groupedMatches = matches.reduce((groups, match) => {
    const sport = (match.sport || 'Unknown Sport').trim();
    const eventName = (match.eventName || match.league || '').trim();
    const sportKey = sport ? sport.charAt(0).toUpperCase() + sport.slice(1) : 'Unknown';
    if (!groups[sportKey]) groups[sportKey] = {};
    const eventKey = eventName || 'General';
    if (!groups[sportKey][eventKey]) groups[sportKey][eventKey] = [];
    groups[sportKey][eventKey].push(match);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      <style jsx>{`
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .date-scroll-container { scrollbar-width: none; -ms-overflow-style: none; scroll-behavior: smooth; }
        .date-scroll-container::-webkit-scrollbar { display: none; }
        .floating-back-btn {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform: ${showBackButton ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)'};
          opacity: ${showBackButton ? '1' : '0'};
          pointer-events: ${showBackButton ? 'auto' : 'none'};
        }
      `}</style>

      {/* Top back button */}
      <div className="bg-white border-b border-gray-200 px-3 py-2">
        <div className="max-w-7xl mx-auto">
<button
  onClick={() => navigate('/')}
  className="inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all"
>
  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
  <span className="sm:hidden">Back</span>
  <span className="hidden sm:inline">Back to Home</span>
</button>

        </div>
      </div>

      {/* Floating back button */}
      <div className="fixed bottom-6 left-6 z-50 floating-back-btn">
        <button
          onClick={() => navigate('/')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-2 sm:px-3 sm:py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center group"
          aria-label="Back"
        >
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm font-medium hidden sm:block">Back to Home</span>
        </button>
      </div>

      {/* Header + filters + date strip */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="py-3">
            {/* Mobile title */}
            <div className="block sm:hidden mb-2">
              <div className="text-center mb-1">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
                  <VoteIcon className="w-5 h-5" />
                  Match Vote
                </h1>
              </div>

              <div className="flex justify-center mb-1">
                <div className="flex items-center space-x-2">
                  <label htmlFor="datePicker" className="text-xs font-medium text-gray-700">Quick:</label>
                  <input
                    id="datePicker"
                    type="date"
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={dateRange[dateRange.length - 1]?.date}
                    min={dateRange[0]?.date}
                  />
                </div>
              </div>

              <div className="flex items-center justify-center space-x-3">
                <p className="text-[11px] text-gray-600">Vote & track live scores</p>
                <div className="flex items-center space-x-1.5 text-[11px] text-gray-500">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live</span>
                </div>
              </div>
            </div>

            {/* Desktop title */}
            <div className="hidden sm:block mb-2">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="order-2 lg:order-1 lg:flex-1 flex items-center justify-center lg:justify-start">
                  <div className="flex items-center space-x-1.5 text-xs text-gray-500">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Live</span>
                  </div>
                </div>

                <div className="order-1 lg:order-2 text-center">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center justify-center gap-2">
                  <VoteIcon className="w-6 h-6 sm:w-7 sm:h-7" />
                  Match Vote
                </h1>
                <p className="text-xs text-gray-600">Vote & track live scores</p>
                </div>

                <div className="order-3 lg:order-3 lg:flex-1 flex items-center justify-center lg:justify-end">
                  <div className="flex flex-wrap items-center justify-center lg:justify-end gap-2">
                  <label htmlFor="datePickerDesktop" className="text-xs font-medium text-gray-700">Quick:</label>
                  <input
                    id="datePickerDesktop"
                    type="date"
                    className="w-full max-w-[160px] border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={dateRange[dateRange.length - 1]?.date}
                    min={dateRange[0]?.date}
                  />
                </div>
              </div>
            </div>
            </div>

            {/* Date strip */}
            <div className="mb-2">
              <div className="flex items-center justify-between gap-1 sm:gap-2">
                <button
                  className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                  onClick={() => {
                    const container = document.querySelector('.date-scroll-container');
                    container.scrollBy({ left: -300, behavior: 'smooth' });
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="flex min-w-0 space-x-1 overflow-x-auto hide-scrollbar date-scroll-container flex-1 mx-1 sm:mx-2">
                  {dateRange.map((dateInfo) => {
                    const showYear = dateInfo.dayNum === 1 && dateInfo.monthName === 'JAN';
                    return (
                      <div key={dateInfo.date} className="flex-shrink-0">
                        {showYear && (
                          <div className="text-center mb-1">
                            <span className="text-xs font-bold text-blue-600 bg-blue-100 px-1 py-0.5 rounded">
                              {dateInfo.year}
                            </span>
                          </div>
                        )}
                        <button
                          onClick={() => setSelectedDate(dateInfo.date)}
                          data-is-today={dateInfo.isToday}
                          className={`flex flex-col items-center px-1.5 sm:px-2 py-1.5 min-w-[44px] sm:min-w-[50px] rounded transition-all duration-200 ${
                            selectedDate === dateInfo.date
                              ? 'bg-blue-600 text-white shadow-sm transform scale-[1.03]'
                              : dateInfo.isToday
                              ? 'bg-gray-200 text-gray-900 font-semibold border border-blue-400'
                              : 'hover:bg-gray-100 text-gray-600'
                          }`}
                        >
                          <span className="text-xs font-semibold">{dateInfo.dayName}</span>
                          <span className="text-base font-bold">{dateInfo.dayNum}</span>
                          <span className="text-xs">{dateInfo.monthName}</span>
                          {dateInfo.isToday && <div className="w-1 h-1 bg-blue-500 rounded-full mt-0.5 animate-pulse"></div>}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                  onClick={() => {
                    const container = document.querySelector('.date-scroll-container');
                    container.scrollBy({ left: 300, behavior: 'smooth' });
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Quick controls */}
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-1">
              <button
                onClick={scrollToToday}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-0.5 rounded border border-blue-300 hover:bg-blue-50 transition-colors"
              >
                Today
              </button>

              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="max-w-full min-w-[120px] px-3 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {sports.map((sport) => (
                  <option key={sport.value} value={sport.value}>
                    {sport.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3">
        <div className="mb-3">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">{formatDateForDisplay(selectedDate)}</h2>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-gray-600 text-sm">Loading matches...</p>
          </div>
        ) : Object.keys(groupedMatches).length > 0 ? (
          <div className="space-y-5">
            {Object.entries(groupedMatches).map(([sportName, sportEvents]) => (
              <div key={sportName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-white px-3 sm:px-4 py-2 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">{sportName}</h3>
                </div>

                <div className="divide-y divide-gray-200">
                  {Object.entries(sportEvents).map(([eventName, eventMatches]) => (
                    <div key={`${sportName}-${eventName}`}>
                      {eventName !== 'General' && (
                        <div className="bg-gray-50 px-3 sm:px-4 py-1.5 border-b border-gray-200">
                          <h4 className="text-sm font-medium text-gray-800">{eventName}</h4>
                        </div>
                      )}

                      {/* Table header - desktop */}
                      <div className="hidden md:block bg-gray-100 px-3 sm:px-4 py-1">
                        <div className="grid grid-cols-12 gap-4 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          <div className="col-span-5">Match</div>
                          <div className="col-span-2 text-center">Result/Time</div>
                          <div className="col-span-3">Event Info</div>
                          <div className="col-span-2 text-center">Votes</div>
                        </div>
                      </div>

                      <div className="divide-y divide-gray-200">
                        {eventMatches.map((match) => {
                          const { status: cStatus, endTime: cEndTime } = getComputedStatusAndEnd(
                            match.status,
                            match.startTime,
                            match.endTime
                          );

                          const isSofaScore = match.source === 'sofascore';

                          // Handler for SofaScore match click - creates match in DB first
                          const handleSofaScoreClick = async (e) => {
                            e.preventDefault();
                            try {
                              const response = await fetch(`${API_BASE_URL}/api/matches/from-sofascore`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  sofaScoreId: match.sofaScoreId,
                                  matchData: {
                                    sport: match.sport,
                                    eventName: match.eventName,
                                    league: match.league,
                                    teamA: match.teamA,
                                    teamB: match.teamB,
                                    teamALogo: match.teamALogo,
                                    teamBLogo: match.teamBLogo,
                                    startTime: match.startTime,
                                    endTime: match.endTime,
                                    status: cStatus,
                                    result: match.result,
                                    eventType: match.eventType,
                                    venue: match.venue
                                  }
                                })
                              });
                              
                              if (response.ok) {
                                const createdMatch = await response.json();
                                navigate(`/match/${createdMatch._id}`);
                              } else {
                                console.error('Failed to create match');
                              }
                            } catch (error) {
                              console.error('Error creating match from SofaScore:', error);
                            }
                          };

                          return (
                            <div
                              key={match._id}
                              onClick={isSofaScore ? handleSofaScoreClick : () => navigate(`/match/${match._id}`)}
                              className="block p-2 sm:p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              {/* Mobile */}
                              <div className="md:hidden space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2 flex-1">
                                    <TeamImage logo={match.teamALogo} flag={match.teamAFlag} alt={match.teamA} className="w-6 h-6" />
                                    <span className="font-semibold text-blue-600 truncate text-sm">{match.teamA}</span>
                                  </div>

                                  <div className="flex items-center space-x-2 px-2">
                                    {getStatusBadge(cStatus, match.startTime, cEndTime)}
                                    {cStatus === 'ended' &&
                                      match.result?.teamAScore != null &&
                                      match.result?.teamBScore != null && (
                                        <span className="text-sm font-bold">
                                          {match.result.teamAScore}-{match.result.teamBScore}
                                        </span>
                                      )}
                                    {cStatus === 'live' &&
                                      match.result?.teamAScore != null &&
                                      match.result?.teamBScore != null && (
                                        <span className="text-sm font-bold text-red-600">
                                          {match.result.teamAScore}-{match.result.teamBScore}
                                        </span>
                                      )}
                                  </div>

                                  <div className="flex items-center space-x-2 flex-1 justify-end">
                                    <span className="font-semibold text-green-600 truncate text-sm">{match.teamB}</span>
                                    <TeamImage logo={match.teamBLogo} flag={match.teamBFlag} alt={match.teamB} className="w-6 h-6" />
                                  </div>
                                </div>

                                {(match.eventType || match.venue) && (
                                  <p className="text-xs text-gray-600 text-center">{match.eventType || match.venue}</p>
                                )}

                                <div className="text-center flex items-center justify-center space-x-2">
                                  <div className="text-[11px] text-gray-500">
                                    {match.totalVotes || 0} votes • {match.comments?.length || 0} comments
                                  </div>
                                </div>
                              </div>

                              {/* Desktop */}
                              <div className="hidden md:grid md:grid-cols-12 md:gap-2 lg:gap-4 md:items-center">
                                <div className="col-span-5">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2 lg:space-x-3 min-w-0">
                                      <TeamImage logo={match.teamALogo} flag={match.teamAFlag} alt={match.teamA} className="w-6 h-6" />
                                      <span className="font-semibold text-blue-600 text-sm truncate">{match.teamA}</span>
                                    </div>
                                    <div className="text-center px-3">
                                      <span className="text-sm font-bold text-gray-600">
                                        {(cStatus === 'ended' || cStatus === 'live') && match.result?.teamAScore != null
                                          ? `${match.result.teamAScore}-${match.result.teamBScore}`
                                          : 'v'}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2 lg:space-x-3 min-w-0">
                                      <span className="font-semibold text-green-600 text-sm truncate">{match.teamB}</span>
                                      <TeamImage logo={match.teamBLogo} flag={match.teamBFlag} alt={match.teamB} className="w-6 h-6" />
                                    </div>
                                  </div>
                                </div>

                                <div className="col-span-2 text-center flex flex-col items-center justify-center">
                                  {getStatusBadge(cStatus, match.startTime, cEndTime)}
                                </div>

                                <div className="col-span-3">
                                  <div className="text-sm text-gray-600 truncate">{match.eventType || match.venue || 'TBD'}</div>
                                  {match.attendance && (
                                    <div className="text-xs text-gray-500">Att: {match.attendance.toLocaleString()}</div>
                                  )}
                                </div>

                                <div className="col-span-2 text-center">
                                  <div className="text-sm font-semibold text-blue-600">Click to Vote</div>
                                  <div className="text-xs text-gray-500">
                                    {match.totalVotes || 0} votes • {match.comments?.length || 0} comments
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="mb-3">
              <VoteIcon className="w-16 h-16 mx-auto opacity-50" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No matches found</h3>
            <p className="text-gray-600 mb-3 text-sm">
              {selectedDate || selectedSport !== 'all'
                ? 'Try selecting a different date or sport to see more matches.'
                : 'No matches have been scheduled yet.'}
            </p>
            <button
              onClick={() => {
                setSelectedSport('all');
                const today = dateRange.find((d) => d.isToday);
                if (today) setSelectedDate(today.date);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Keep outside component to avoid re-creation on every render
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selectedDateTime = new Date(dateString + 'T00:00:00').getTime();
  const todayTime = today.getTime();
  if (selectedDateTime === todayTime) return 'Today';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TeamImage = ({ logo, flag, alt, className = 'w-6 h-6' }) => {
  const [imageError, setImageError] = useState(false);

  const getImageUrl = () => {
    if (logo) return logo;
    if (flag && typeof flag === 'object' && flag.image) return flag.image;
    if (flag && typeof flag === 'string') return flag;
    return null;
  };

  const imageUrl = getImageUrl();
  if (imageError || !imageUrl) {
    return (
      <div className={`${className} bg-gray-200 rounded-full flex items-center justify-center`}>
        <span className="text-gray-500 text-[10px] font-bold">{alt ? alt.charAt(0).toUpperCase() : '?'}</span>
      </div>
    );
  }

  return (
    <div className={`${className} flex items-center justify-center relative`}>
      <img
        src={imageUrl}
        alt={alt}
        className={`${className} object-contain rounded`}
        onError={() => setImageError(true)}
        loading="lazy"
      />
    </div>
  );
};

const VoteIcon = ({ className = 'w-5 h-5' }) => <img src="/images/vote.png" alt="Vote" className={className} />;

export default MatchVote;
